/**
 * The signed-in person's own alarm work — CRATUS 2.0 #19, "a notification bar for task and alarm
 * assignments to the signed-in user". Pure, tested in `my-work.test.ts`.
 *
 * MY WORK is every alarm assigned to me whose work is not finished: assigned, acknowledged, in
 * progress, blocked, or sent back by the platform's verification. It is the server's filter
 * (`assignee=me`), so the count is not a cut of whatever page another screen happened to load.
 *
 * NEW is what was not there the last time this browser looked — never everything on the first
 * look, which would toast a week of standing work at every sign-in.
 */
import type { AlarmRow } from '@/lib/api/types';

/** The lifecycle states that are still somebody's to do. */
export const MY_WORK_LIFECYCLE = 'assigned,acknowledged,in_progress,blocked,verification_failed';

const STATE_LABEL: Partial<Record<AlarmRow['state'], string>> = {
  assigned: 'Assigned to you',
  acknowledged: 'Acknowledged',
  in_progress: 'In progress',
  blocked: 'Blocked',
  verification_failed: 'Sent back — not verified',
};

export function workStateLabel(state: AlarmRow['state']): string {
  return STATE_LABEL[state] ?? state;
}

/** The alarms that are new since `known`; none at all on the first look (`known` null). */
export function newAssignments(known: ReadonlySet<string> | null, rows: readonly AlarmRow[]): AlarmRow[] {
  if (known === null) return [];
  return rows.filter((a) => !known.has(a.id));
}

/** Blocked and sent-back work first — it is waiting on somebody — then the oldest. */
export function rankMyWork(rows: readonly AlarmRow[]): AlarmRow[] {
  const urgent = (a: AlarmRow): number => (a.state === 'blocked' || a.state === 'verification_failed' ? 0 : a.state === 'assigned' ? 1 : 2);
  return [...rows].sort((a, b) => urgent(a) - urgent(b) || a.openedAt.localeCompare(b.openedAt) || a.id.localeCompare(b.id));
}
